import React, { ChangeEvent, useCallback, useEffect } from "react";
import SelectHoc from "../../common/SelectHoc";
import { getSelectedValue } from "../../services/utils/utils";
import { RadioGroup, Radio, Input } from "@citi-icg-172888/icgds-react";
import { useData } from "../../services/context/DataContext";
import { ICompanyInfo } from "../../services/types/LeadRequisitionType";
import DropdownMultipleHoc from "../../common/DropdownMultipleHoc";

interface ISelectHocProps {
  errorIndustryType?: string;
  errorSalesSize?: string;
  errorVcPe?: string;
  errorWebsite?: string;
  validateFields: (
    updatedCompanyData: ICompanyInfo | null,
    updatedTouchedFields?: Partial<Record<string, boolean>>
  ) => boolean;
  setTouchedFields: React.Dispatch<React.SetStateAction<Partial<Record<string, boolean>>>>;
  tochedFields?: Partial<Record<string, boolean>>;
}

const SalesSize: React.FC<ISelectHocProps> = ({
  errorIndustryType,
  errorSalesSize,
  errorVcPe,
  errorWebsite,
  validateFields,
  setTouchedFields,
  tochedFields,
}) => {
  const { companyInfo, setCompanyInfo, staticData } = useData();
  const { Industries, SalesSize } = getSelectedValue(companyInfo, staticData);
  const divRef = React.useRef<HTMLDivElement>(null);

  const onChangeDDHandler = useCallback(
    (options: string[], ddName: string, onChangeCalled?: boolean) => {
      const updatedCompanyInfo: any = {
        ...companyInfo,
        [ddName]: options,
      };
      if (ddName === "industry") {
        updatedCompanyInfo.salesSize = "";
        updatedCompanyInfo.isCompanyVCorPEBacked = "";
      }
      if (ddName === "salesSize") {
        updatedCompanyInfo.isCompanyVCorPEBacked = "";
      }
      if (!onChangeCalled && ddName === "operatedCountry") {
        divRef.current?.focus();
      }

      setCompanyInfo(updatedCompanyInfo);
      setTouchedFields((prev) => {
        const newTouchedFields = { ...prev, [ddName]: true };
        validateFields(updatedCompanyInfo, newTouchedFields);
        return newTouchedFields;
      });
    },
    [companyInfo, setCompanyInfo]
  );

  useEffect(() => {
    if (companyInfo?.salesSize === "Pre-Revenue") {
      const radioGroup = document.querySelectorAll(".lmn-selection-control");

      radioGroup.forEach((marker) => {
        marker.setAttribute("tabindex", "0");
        marker.addEventListener("keydown", (event: any) => {
          const options = ["Yes", "No"];
          const currentIndex = options.indexOf(event.target.textContent);
          if (event.key === "Enter" && event.target.textContent) {
            const mockEvent = {
              target: {
                name: "isCompanyVCorPEBacked",
                value: event.target.textContent, // Use the textContent as the value
              },
            } as ChangeEvent<HTMLInputElement>;
            onChangeHandler(mockEvent);
          } else if (event.key === "ArrowUp" && event.target.textContent) {
            const prevIndex = currentIndex > 0 ? currentIndex - 1 : currentIndex;
            const prevMarker = radioGroup[prevIndex] as HTMLInputElement;
            if (prevMarker) {
              prevMarker.focus();
            }
            const mockEvent = {
              target: {
                name: "isCompanyVCorPEBacked",
                value: options[prevIndex], // Use the textContent as the value
              },
            } as ChangeEvent<HTMLInputElement>;
            onChangeHandler(mockEvent);
          } else if (event.key === "ArrowDown" && event.target.textContent) {
            const nextIndex = currentIndex < options.length - 1 ? currentIndex + 1 : currentIndex;
            const nextMarker = radioGroup[nextIndex] as HTMLInputElement;
            if (nextMarker) {
              nextMarker.focus();
            }
            const mockEvent = {
              target: {
                value: options[nextIndex], // Use the textContent as the value
              },
            } as ChangeEvent<HTMLInputElement>;
            onChangeHandler(mockEvent);
          }
        });
      });
    }
  }, [companyInfo?.salesSize === "Pre-Revenue"]);

  const onChangeHandler = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const { name, value } = event.target;
      const updatedCompanyData: any = {
        ...companyInfo,
        [name]: value,
      };

      setCompanyInfo(updatedCompanyData);
      tochedFields?.website &&
        setTouchedFields((prev) => {
          const newTouchedFields = { ...prev, ["website"]: true };
          validateFields(updatedCompanyData, newTouchedFields);
          return newTouchedFields;
        });
    },
    [companyInfo, setCompanyInfo]
  );

  const onBlurHandler = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const { name, value } = event.target;
      const updatedCompanyData: any = {
        ...companyInfo,
        [name]: value,
      };

      setTouchedFields((prev) => {
        const newTouchedFields = { ...prev, ["website"]: true };
        validateFields(updatedCompanyData, newTouchedFields);
        return newTouchedFields;
      });
    },
    [companyInfo, setCompanyInfo]
  );

  return (
    <div className="lmn-dnca-w-l-50 lmn-dnca-w-m-50">
      <div
        id="sub-countries"
        className="lmn-form-group lmn-mb-5 lmn-mt-5  lmn-dnca-w-m-100 lmn-dnca-w-s-100"
      >
        <label className="lmn-text-wrap" htmlFor="country">
          Other Countries with Subsidiaries or Affiliates (Optional)
        </label>
        <DropdownMultipleHoc
          id="country"
          isMultiple={true}
          placeholder={"Subsidiaries Countries"}
          label="Other Countries with Subsidiaries or Afiliates (Optional)"
          menuStyle={{ border: "1px solid #255be3", borderRadius: "2px" }}
          options={staticData.jurisdictions}
          onChange={(options: string[] | undefined) =>
            onChangeDDHandler(options as string[], "operatedCountry", true)
          }
          onApply={(options: string[] | undefined) =>
            onChangeDDHandler(options as string[], "operatedCountry")
          }
          value={companyInfo?.operatedCountry}
        />
      </div>

      <div className=" lmn-dnca-w-m-100 lmn-dnca-w-s-100" ref={divRef} tabIndex={-1}>
        <div className="lmn-form-group lmn-mb-5">
          <label className="lmn-required label">Industry Type</label>
          <SelectHoc
            label="Industry Type"
            id="industry_type"
            value={Industries.length > 1 ? companyInfo?.industry : Industries[0]}
            options={Industries || []}
            error={errorIndustryType}
            onChange={(options: any) => onChangeDDHandler(options, "industry")}
          />
          {errorIndustryType && <small>{errorIndustryType}</small>}
        </div>
        <div className="lmn-form-group lmn-mb-5">
          <label className="lmn-required label" htmlFor="website">
            Company Website
          </label>
          <Input
            id="website"
            name="website"
            invalid={!!errorWebsite}
            value={companyInfo?.website}
            onChange={onChangeHandler}
            onFocus={onChangeHandler}
            onBlur={onBlurHandler}
            aria-required={true}
            aria-describedby="website_error"
          />
          {errorWebsite && (
            <div aria-live="assertive" role="alert" id="website_error">
              <small>{errorWebsite}</small>
            </div>
          )}
        </div>
        <div className="lmn-form-group lmn-mb-5">
          <label className="lmn-font-size-xs lmn-required label">Annual Revenue</label>
          <SelectHoc
            label="Annual Revenue"
            id="sales_size"
            value={companyInfo?.salesSize}
            options={SalesSize || []}
            error={errorSalesSize}
            onChange={(options: any) => onChangeDDHandler(options, "salesSize")}
          />
          {errorSalesSize && <small>{errorSalesSize}</small>}
        </div>
        {companyInfo?.salesSize === "Pre-Revenue" && (
          <div className="lmn-form-group lmn-w-75 lmn-mr-5" tabIndex={0}>
            <label className="lmn-font-size-xs label lmn-required" id={`${"groupId"}-label`}>
              Is your company VC/PE backed?
            </label>
            <RadioGroup
              name="isCompanyVCorPEBacked"
              value={companyInfo?.isCompanyVCorPEBacked}
              onChange={onChangeHandler}
              aria-label="Is your company VC/PE backed?"
              aria-required={true}
              aria-invalid={!!errorVcPe}
              aria-describedby={errorVcPe ? `${"groupId"}-error` : null}
            >
              <Radio
                inline
                className="label"
                aria-label="Is your company VC/PE backed? Yes"
                value="Yes"
                id={`${"groupId"}-yes`}
              >
                Yes
              </Radio>
              <Radio
                inline
                className="label"
                aria-label="Is your company VC/PE backed? No"
                value="No"
                id={`${"groupId"}-no`}
              >
                No
              </Radio>
            </RadioGroup>
            {errorVcPe && <small id={`${"groupId"}-error`} role="alert">{errorVcPe}</small>}
          </div>
        )}
      </div>
    </div>
  );
};

export default React.memo(SalesSize);
